import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import type { IEntryPointComponent } from "@/src/common/PageTransition";
import { BrandPageLink } from "@/src/common/links/BrandPageLink";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { FlyerCatalogue, type IFlyerCatalogue } from "@/src/flyers/FlyerCatalogue";
import { preventScrollY } from "@/src/flyers/FlyerCatalogue/FlyerCatalogueHelpers";
import {
    FlyerDetailsResources,
    type IFlyerDetailsResources,
} from "@/src/flyers/FlyerDetails/FlyerDetails.Resources";
import { FlyerDetailsResourcesEn } from "@/src/flyers/FlyerDetails/FlyerDetails.Resources.En";
import { FlyerDetailsContextProvider } from "@/src/flyers/FlyerDetails/FlyerDetailsContext";
import { NotFound } from "@/src/pages/not-found";
import { useQueryPageCustom } from "@/src/query-client-factory";
import PageLayout from "@/src/shared/page-layout";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax } from "@Components/ComponentAjax";
import useAjax from "@Components/Hooks/useAjax";
import { FlyersPage } from "@Routes/FlyersLinks";
import * as stylex from "@stylexjs/stylex";

import { genericStyles } from "../../../styles/tokens.stylex";
import type { IActionButtonBanner } from "../../common/ActionButtonBanner";
import ContentContainer, { type IContentContainer } from "../../common/ContentContainer";
import type { IImageBanner } from "../../common/ImageBanner";
import PageTags from "../../common/PageTags";
import DiscountBanner, { type IDiscountBanner } from "../../discount/DiscountBanner";
import { DiscountHeader, type IDiscountHeader } from "../../discount/DiscountHeader";
import { DiscountNavigation, type IDiscountNavigation } from "../../discount/DiscountNavigation";

import * as portalCss from "../../shared/Portal.module.css";
import * as css from "./FlyerDetails.module.css";

export type IFlyerDetails = IEntryPointComponent &
    IComponentAjax<IFlyerDetailsResult> & {
        id: string;
        brandId: string;
        brandName: string;
        allDiscountsLabel: string;
        allDiscountsText: string;
        allDiscountsLink: string;
        validityText: string;
        discountHeader: IDiscountHeader;
        catalogue: IFlyerCatalogue;
        brandWebsiteLink: string;
        showFlyerText: string;
        navigation: IDiscountNavigation;
        flyerImage: IImageBanner;
        invalidFlyer: boolean;
        goBack: unknown;
        likeAndShare: IActionButtonBanner;
        contentContainers?: IContentContainer;
        tags?: string[];
        isMobileOrTablet: boolean;
        canonicalUrl?: string;
        resources: IFlyerDetailsResources;
    };

type IFlyerDetailsResult = {
    isDiscountSaved: boolean;
    showNewsletterSignupModal: boolean;
    isBrandObserved: boolean;
    score: number;
};

type IFlyerDetailsState = {
    isDiscountSaved: boolean;
    isBrandObserved: boolean;
    showNavigation: boolean;
    showReviewModal: boolean;
};

export function FlyerDetails() {
    const { data } = useQueryPageCustom<IFlyerDetails>({ ignoreQuery: true });
    const props = data.component;

    const { isMobileOrTablet } = useViewportInfoContext();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const resources = languageSelector(FlyerDetailsResources, FlyerDetailsResourcesEn);

    const [state, setState] = useState<IFlyerDetailsState>({
        isDiscountSaved: false,
        isBrandObserved: false,
        showNavigation: !!searchParams.get(props.navigation?.storageKeyParameterName),
        showReviewModal: false,
    });

    const { request } = useAjax<IFlyerDetailsResult>({
        ajaxAction: props.ajaxAction,
        initialData: props.initialData,
        success: (response) => {
            setState({
                ...state,
                isDiscountSaved: response.isDiscountSaved || state.isDiscountSaved,
                isBrandObserved: response.isBrandObserved || state.isBrandObserved,
            });
        },
    });

    useEffect(() => {
        request({ origin: searchParams.get("origin") });

        document.addEventListener("onwheel", preventScrollY);

        return () => {
            document.removeEventListener("onwheel", preventScrollY);
        };
    }, []);

    const discountImage: () => IDiscountBanner = () => ({
        id: props.id,
        likeAndShare: {
            ...props.likeAndShare,
            isDiscountSaved: state.isDiscountSaved,
            share: {
                showTooltip: true,
                text: `${props.discountHeader.title} : ${props.discountHeader.caption || ""} ${props.discountHeader.title}`,
                shareUrl: pathname,
                title: `Goodie - ${props.discountHeader.title}`,
                appearance: "dark",
            },
        } as IActionButtonBanner,
        discountImage: props.flyerImage,
    });

    const dHeader = props.discountHeader;

    if (data.component.componentName === "NotFound") {
        return <NotFound />;
    }

    return (
        <PageLayout
            layoutAdUnitPath="gazetki"
            slugTarget={pathname}
            breadcrumbs={[
                { labelKey: "flyersTab", path: FlyersPage },
                {
                    label: dHeader.brandName,
                    path: dHeader.logoTile.href,
                },
                {
                    label: dHeader.caption ? `${dHeader.caption} ${dHeader.title}` : dHeader.title,
                },
            ]}
            key={pathname}
        >
            <div
                {...defaultAttributes(
                    props,
                    css.flyerDetailsComponent,
                    props.invalidFlyer && css.invalid,
                    portalCss.standardContainerV2,
                    portalCss.containerMargins,
                )}
            >
                <DiscountBanner {...discountImage()} />
                <div className={portalCss.setSectionColOne}>
                    {(props.navigation || state.showNavigation) && (
                        <DiscountNavigation {...props.navigation} />
                    )}
                    <DiscountHeader
                        {...props.discountHeader}
                        isDiscountSaved={state.isDiscountSaved}
                        isBrandObserved={state.isBrandObserved}
                        title={props.discountHeader.title}
                        caption={props.discountHeader.caption}
                        validityText={props.validityText}
                    />
                    {isMobileOrTablet && props.validityText && (
                        <span
                            className={css.validityText}
                            dangerouslySetInnerHTML={{ __html: props.validityText }}
                        />
                    )}
                    <div className={`${css.infoWrapper} ${stylex.props(styles.border).className}`}>
                        <div className={css.checkSection}>
                            <div className={css.availableButtonsLabel}>
                                {resources.allOffersBrand.replace("{0}", props.brandName)}
                            </div>
                            {props.allDiscountsLink && (
                                <BrandPageLink
                                    text={props.allDiscountsText}
                                    href={props.allDiscountsLink}
                                    brandId={props.brandId}
                                    size="M"
                                    origin="FlyerDetails"
                                />
                            )}
                        </div>
                    </div>
                    <div className={css.infoWrapperDivision} />
                    <FlyerDetailsContextProvider>
                        <FlyerCatalogue
                            {...props.catalogue}
                            flyerId={props.id}
                            brandName={props.brandName}
                            canonicalUrl={pathname}
                            caption={props.discountHeader.caption}
                            title={props.discountHeader.title}
                            hideNavigation={state.showNavigation}
                            brandUrlSeo={props.allDiscountsLink!}
                        />
                    </FlyerDetailsContextProvider>
                    <ContentContainer {...props.contentContainers} />
                    {props.tags && <PageTags tags={props.tags} />}
                </div>
            </div>
        </PageLayout>
    );
}

const styles = stylex.create({
    border: {
        borderRadius: genericStyles.borderRadiusS1,
    },
});
